import { useEffect, useState } from "react";
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Dialog,
    IconButton,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { enqueueSnackbar } from "notistack";
import {
    Campaign,
    ExperimentStrategy,
    useCreateExperimentMutation,
    useGetCampaignKeywordsQuery,
    useGetCampaignStrategyQuery,
    useGetExperimentCampaignsQuery,
} from "src/context/api/bidStratTesting/api";
import { BACKDROP_SX, COLORS, BlackButton, Icon } from "./components";
import StrategyStep, { EMPTY_STRATEGY_PARAMS, MAX_CPC_STRATEGY, StrategyParams, strategyErrors, strategyPayload } from "./strategy-step";
import { DEFAULT_COMPARISON_WEEKS, apiErrorMessage, isEndDatePast, parisToday, toIsoDate, unreliableComparison } from "./utils";
import { tr } from "./i18n";

/** Error shown to the OPS, in the page language when the API gives its code. */
function switchErrorMessage(error: any): string {
    const code = error?.data?.code;
    const failed = error?.data?.experiment;
    if (code === "strategy_validation_failed") {
        // "Google Ads refused this strategy: {Google Ads message}"
        const details = String(error?.data?.error ?? "").replace(/^[^:]*:\s*/, "");
        return tr("modal.errors.strategy_validation_failed", { details });
    }
    const translated = apiErrorMessage(error);
    if (translated) return translated;
    if (code !== "switch_failed" || !failed) {
        return error?.data?.error ?? tr("modal.create_error");
    }
    const step = tr(`modal.steps.${failed.switch_error_step}`, { defaultValue: failed.switch_error_step });
    const rollback = tr(failed.rollback_status === "failed" ? "modal.rollback_failed" : "modal.rollback_ok");
    return tr("modal.switch_failed", { step, rollback, details: failed.switch_error_message });
}

// Options of the Figma (Lily, bug report #17): under 8 weeks the analysis is unreliable
const WEEK_OPTIONS = [4, 6, 8, 10, 12, 14, 16, 24, 36, 48];


// "Statistical data for this period will be non-reliable." (Figma tooltip)
const TOOLTIP_SX = {
    bgcolor: "white",
    color: COLORS.text,
    fontSize: 14,
    fontWeight: 500,
    lineHeight: 1.4,
    px: 1,
    py: 0.5,
    maxWidth: 190,
    borderRadius: "8px",
    boxShadow: "1px 3px 4px rgba(0, 0, 0, 0.08)",
};

// Outlined fields of the design: 12px label always on the border, 8px radius
const FIELD_SX = {
    // Same 12px label on the border in every field (TextField, Select, DatePicker)
    "& .MuiInputLabel-root": { fontWeight: 600, color: COLORS.muted },
    // Beats the theme's MuiFormLabel shrink override (font.label, filled transform)
    "& .MuiInputLabel-root.MuiInputLabel-shrink": {
        fontSize: 12,
        lineHeight: 1.2,
        fontWeight: 600,
        transform: "translate(14px, -7px) scale(1)",
    },
    "& .MuiInputLabel-root.Mui-focused": { color: COLORS.secondary },
    "& .MuiOutlinedInput-root": { borderRadius: "8px", fontSize: 14, fontWeight: 600, color: COLORS.text, height: 52 },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(145, 158, 171, 0.3)" },
    // The notch is sized at 0.75em of the 14px input (10.5px): match it to the 12px label
    "& .MuiOutlinedInput-notchedOutline legend": { fontSize: 12, fontWeight: 600 },
    "& .MuiOutlinedInput-input::placeholder": { color: COLORS.secondary, opacity: 1 },
};


interface Props {
    open: boolean;
    onClose: () => void;
    workspaceId: number;
    // Raw BigQuery region_name of the selected region tab
    regionName: string;
}

export default function CreateExperimentModal({ open, onClose, workspaceId, regionName }: Props) {
    const [name, setName] = useState("");
    const [campaign, setCampaign] = useState<Campaign | null>(null);
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    const [weeks, setWeeks] = useState<number>(DEFAULT_COMPARISON_WEEKS);
    const [submitError, setSubmitError] = useState<string | null>(null);
    // Lot 1b: step 2, strategy of the test launched from the app
    const [step, setStep] = useState<1 | 2>(1);
    const [strategy, setStrategy] = useState<ExperimentStrategy | "">("");
    const [strategyParams, setStrategyParams] = useState<StrategyParams>(EMPTY_STRATEGY_PARAMS);
    const [showStrategyErrors, setShowStrategyErrors] = useState(false);

    const { data: campaigns, isFetching: campaignsLoading, isError: campaignsError } = useGetExperimentCampaignsQuery(
        { workspace_id: workspaceId, region_name: regionName },
        { skip: !open }
    );
    const [createExperiment, { isLoading: creating }] = useCreateExperimentMutation();
    // Lot 1c: keywords of the campaign for Max CPC (same query as the keyword table)
    const { data: campaignKeywords } = useGetCampaignKeywordsQuery(
        { workspace_id: workspaceId, region_name: regionName, campaign_id: campaign?.campaign_id ?? "" },
        { skip: !open || !campaign || strategy !== MAX_CPC_STRATEGY }
    );

    useEffect(() => {
        if (open) return;
        setName("");
        setCampaign(null);
        setStartDate(null);
        setEndDate(null);
        setWeeks(DEFAULT_COMPARISON_WEEKS);
        setSubmitError(null);
        setStep(1);
        setStrategy("");
        setStrategyParams(EMPTY_STRATEGY_PARAMS);
        setShowStrategyErrors(false);
    }, [open]);

    // The campaign list belongs to one region
    useEffect(() => setCampaign(null), [regionName]);

    const datesValid = !!startDate && !!endDate && toIsoDate(endDate) >= toIsoDate(startDate);
    const endDatePast = isEndDatePast(endDate);
    // "Your current strategy" (G-READ, a few seconds) is read as soon as the campaign
    // is chosen: step 2 shares this request and finds it done
    useGetCampaignStrategyQuery(
        { workspace_id: workspaceId, region_name: regionName, campaign_id: campaign?.campaign_id ?? "" },
        { skip: !open || !campaign || endDatePast }
    );
    const canCreate = !!name.trim() && !!campaign && datesValid && endDatePast && !creating;
    // No end date or a future one: test launched from the app, today (lot 1b) or
    // scheduled on a future start date, switched by the 00:15 job (lot 3)
    // Paris day: the API refuses a start date before its own today (review ticket)
    const today = parisToday();
    const startsTodayOrLater = !!startDate && toIsoDate(startDate) >= today;
    const scheduled = !!startDate && toIsoDate(startDate) > today;
    const canSelectStrategy =
        !endDatePast && !!name.trim() && !!campaign && startsTodayOrLater && (!endDate || datesValid);

    const submit = async (strategyFields?: object) => {
        if (!campaign || !startDate) return;
        setSubmitError(null);
        try {
            await createExperiment({
                workspace_id: workspaceId,
                region_name: regionName,
                name: name.trim(),
                campaign_id: campaign.campaign_id,
                start_date: toIsoDate(startDate),
                end_date: endDate ? toIsoDate(endDate) : null,
                comparison_weeks: weeks,
                ...strategyFields,
            }).unwrap();
            const launched = tr(scheduled ? "modal.scheduled" : "modal.launched");
            enqueueSnackbar(strategyFields ? launched : tr("modal.created"), { variant: "success" });
            onClose();
        } catch (error: any) {
            setSubmitError(switchErrorMessage(error));
        }
    };

    const handleCreate = () => {
        if (canCreate) submit();
    };

    const handleLaunch = () => {
        setShowStrategyErrors(true);
        const keywords = campaignKeywords?.data;
        if (!strategy || Object.keys(strategyErrors(strategy, strategyParams, keywords)).length) return;
        submit(strategyPayload(strategy, strategyParams, keywords));
    };

    // The KO Bidding Auto keyword table (KPI, Threshold) is wider: 1040px as in the design
    const modalWidth = step === 2 && strategy === MAX_CPC_STRATEGY && strategyParams.mode === "KO Bidding Auto" ? 1040 : 800;

    const dateSlotProps = (placeholder?: string, error?: string, hint?: string) => ({
        textField: {
            fullWidth: true,
            InputLabelProps: { shrink: true },
            placeholder,
            error: !!error,
            helperText: error ?? hint,
            // MUI pulls the calendar button 12px to the right (edge="end"): its hover
            // circle touched the border, it keeps 10px from it
            sx: { ...FIELD_SX, "& .MuiInputAdornment-positionEnd .MuiIconButton-root": { mr: "-4px" } },
        },
    });

    return (
        <Dialog
            slotProps={{ backdrop: { sx: BACKDROP_SX } }}
            open={open}
            onClose={onClose}
            maxWidth={false}
            PaperProps={{ sx: { width: modalWidth, maxWidth: "calc(100vw - 32px)", borderRadius: "16px", boxShadow: "0px 2px 10px 0px rgba(0, 0, 0, 0.3)", m: 2 } }}
        >
            <Stack
                direction="row"
                alignItems="center"
                sx={{ px: 3, py: 2.5, bgcolor: "rgba(226, 229, 233, 0.16)", borderBottom: `1px solid ${COLORS.border}` }}
            >
                <Typography sx={{ flex: 1, fontSize: 18, fontWeight: 500, lineHeight: 1, color: COLORS.text }}>
                    {tr("modal.title")}
                </Typography>
                <IconButton aria-label={tr("common.close")} onClick={onClose} size="small" sx={{ p: 0.5 }}>
                    <Icon name="x-close" size={16} />
                </IconButton>
            </Stack>

            <Stack gap={2.5} sx={{ p: 3 }}>
                {step === 2 && campaign && (
                    <StrategyStep
                        workspaceId={workspaceId}
                        regionName={regionName}
                        campaignId={campaign.campaign_id}
                        strategy={strategy}
                        onStrategyChange={(value) => {
                            setStrategy(value);
                            setShowStrategyErrors(false);
                        }}
                        params={strategyParams}
                        onParamsChange={setStrategyParams}
                        showErrors={showStrategyErrors}
                        fieldSx={FIELD_SX}
                        searchCampaign={(campaign.advertising_channel_type ?? "").toLowerCase() === "search"}
                    />
                )}
                {step === 1 && (
                <>
                <TextField
                    label={tr("modal.name")}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    inputProps={{ maxLength: 255 }}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                    sx={FIELD_SX}
                />
                <Autocomplete
                    options={campaigns?.data ?? []}
                    loading={campaignsLoading}
                    value={campaign}
                    onChange={(_e, value) => {
                        // The strategy and its keyword values belong to the previous campaign
                        // (Max CPC is only offered for a Search campaign)
                        if (value?.campaign_id !== campaign?.campaign_id) {
                            setStrategy("");
                            setStrategyParams(EMPTY_STRATEGY_PARAMS);
                        }
                        setCampaign(value);
                    }}
                    getOptionLabel={(option) => option.campaign_name || option.campaign_id}
                    isOptionEqualToValue={(option, value) => option.campaign_id === value.campaign_id}
                    popupIcon={<Icon name="selector-open" size={18} />}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={tr("modal.campaign")}
                            InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
                            error={campaignsError}
                            helperText={campaignsError ? tr("modal.campaigns_error") : undefined}
                            sx={FIELD_SX}
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {campaignsLoading && <CircularProgress size={18} />}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                        />
                    )}
                />
                <Stack direction="row" gap={2} alignItems="flex-start">
                    {/* Calendar icon of the theme, as on every date picker of the app */}
                    <DatePicker
                        label={tr("modal.start_date")}
                        value={startDate}
                        onChange={setStartDate}
                        format="do MMM yyyy"
                        // No hint under the start date (Lily): with a past start date, the end
                        // date decides, a past one creates an already completed experiment
                        slotProps={dateSlotProps() as any}
                    />
                    <DatePicker
                        label={tr("modal.end_date")}
                        value={endDate}
                        onChange={setEndDate}
                        format="do MMM yyyy"
                        minDate={startDate ?? undefined}
                        slotProps={
                            dateSlotProps(
                                tr("common.optional"),
                                !!startDate && !!endDate && !datesValid ? tr("modal.end_before_start") : undefined
                            ) as any
                        }
                    />
                    {/* Autocomplete of the app, the theme's list; the value as text (no chip) */}
                    <Autocomplete
                        options={WEEK_OPTIONS}
                        value={weeks}
                        onChange={(_e, value) => value && setWeeks(value)}
                        disableClearable
                        fullWidth
                        getOptionLabel={(option) => tr("modal.weeks", { count: option })}
                        popupIcon={<Icon name="selector-open" size={18} />}
                        // The list opens below the field, which stays visible (bug report #18)
                        slotProps={{ popper: { placement: "bottom-start" } }}
                        renderOption={(props, option) => (
                            <Box component="li" {...props} key={option} sx={{ gap: 1 }}>
                                <Box component="span" sx={{ flex: 1 }}>
                                    {tr("modal.weeks", { count: option })}
                                </Box>
                                {option < DEFAULT_COMPARISON_WEEKS && (
                                    <Tooltip title={tr("modal.non_reliable")} placement="right" componentsProps={{ tooltip: { sx: TOOLTIP_SX } }}>
                                        <Box component="span" sx={{ display: "flex" }} aria-label={tr("modal.non_reliable")}>
                                            <Icon name="alert-triangle" size={14} />
                                        </Box>
                                    </Tooltip>
                                )}
                            </Box>
                        )}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={tr("modal.comparison_period")}
                                InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
                                helperText={weeks < DEFAULT_COMPARISON_WEEKS ? unreliableComparison() : undefined}
                                FormHelperTextProps={{ sx: { color: "warning.main", mx: 0.5 } }}
                                inputProps={{ ...params.inputProps, readOnly: true }}
                                sx={{ ...FIELD_SX, "& .MuiAutocomplete-input": { cursor: "pointer" } }}
                            />
                        )}
                    />
                </Stack>
                </>
                )}
                {submitError && <Alert severity="error">{submitError}</Alert>}
            </Stack>

            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                sx={{ px: 2, py: 1.5, borderTop: `1px solid ${COLORS.border}` }}
            >
                <Button
                    onClick={() => {
                        if (step === 2) {
                            setStep(1);
                            setSubmitError(null);
                        } else {
                            onClose();
                        }
                    }}
                    disabled={creating}
                    sx={{ color: COLORS.muted, fontSize: 13, fontWeight: 600, lineHeight: "18px", px: 1.5, py: 0.75, minWidth: 0 }}
                >
                    {tr("modal.back")}
                </Button>
                {step === 1 && endDatePast && (
                    <BlackButton
                        onClick={handleCreate}
                        disabled={!canCreate}
                        endIcon={creating ? <CircularProgress size={12} color="inherit" /> : <Icon name="arrow-right" size={12} />}
                        sx={{ pr: 1, "& .MuiButton-endIcon": { ml: 1 }, "&.Mui-disabled": { bgcolor: "rgba(24, 24, 24, 0.4)", color: "white" } }}
                    >
                        {tr("modal.create_experiment")}
                    </BlackButton>
                )}
                {step === 1 && !endDatePast && (
                    <BlackButton
                        onClick={() => setStep(2)}
                        disabled={!canSelectStrategy}
                        endIcon={<Icon name="arrow-right" size={12} />}
                        sx={{ pr: 1, "& .MuiButton-endIcon": { ml: 1 }, "&.Mui-disabled": { bgcolor: "rgba(24, 24, 24, 0.4)", color: "white" } }}
                    >
                        {tr("modal.select_strategy")}
                    </BlackButton>
                )}
                {step === 2 && (
                    <BlackButton
                        onClick={handleLaunch}
                        disabled={!strategy || creating}
                        endIcon={creating ? <CircularProgress size={12} color="inherit" /> : undefined}
                        sx={{ "&.Mui-disabled": { bgcolor: "rgba(24, 24, 24, 0.4)", color: "white" } }}
                    >
                        {tr("modal.create")}
                    </BlackButton>
                )}
            </Stack>
        </Dialog>
    );
}
